import { LIBRARY_STATUSES, LIBRARY_STATUS_LABELS_PL, type LibraryStatus } from "@anisync/types";
import { useEffect, useState } from "react";
import { getLibrary, setLibraryEntry } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

/**
 * Status serii w bibliotece.
 *
 * Wybór zapisuje się od razu, bez przycisku zatwierdzania — to jedno pole,
 * a osobne „Zapisz" tylko dodawałoby krok. Zapis oznacza wpis jako ręczny, więc
 * dalsze oglądanie już go nie nadpisze.
 */
export function LibraryStatusPicker({ titleId }: { titleId: string }) {
  const [status, setStatus] = useState<LibraryStatus | "">("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setStatus("");
    // Biblioteka wraca w całości; przy jednym tytule wystarczy z niej wybrać
    // interesujący wpis, a osobny endpoint na jeden rekord nic by nie wniósł.
    getLibrary()
      .then((data) => {
        if (cancelled) return;
        const entry = data.items.find((e) => e.titleId === titleId);
        setStatus(entry?.status ?? "");
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [titleId]);

  function change(next: string): void {
    if (next === "") return;
    const value = next as LibraryStatus;
    const previous = status;
    setStatus(value);
    setSaving(true);
    setError(false);

    setLibraryEntry(titleId, { status: value })
      .catch(() => {
        // Cofamy wybór: pole pokazujące stan, którego nie ma w bazie, jest
        // gorsze niż widoczny błąd.
        setStatus(previous);
        setError(true);
      })
      .finally(() => setSaving(false));
  }

  return (
    <div className="mt-1 flex items-center gap-2">
      <span className="text-xs text-muted-foreground" id={`status-label-${titleId}`}>
        W bibliotece
      </span>
      {/* Radix nie ma opcji pustej: element z wartością `''` jest u niego
          nielegalny, bo oznaczałby „brak zaznaczenia". Stan „nie dodano" żyje
          więc jako `placeholder`, a lista zawiera wyłącznie prawdziwe statusy. */}
      <Select
        {...(status === "" ? {} : { value: status })}
        disabled={saving}
        onValueChange={change}
      >
        <SelectTrigger
          className="h-8 min-w-[9rem] text-sm"
          aria-labelledby={`status-label-${titleId}`}
        >
          <SelectValue placeholder="— nie dodano —" />
        </SelectTrigger>
        <SelectContent>
          {LIBRARY_STATUSES.map((s) => (
            <SelectItem key={s} value={s}>
              {LIBRARY_STATUS_LABELS_PL[s]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && <span className="text-xs text-destructive">nie udało się zapisać</span>}
    </div>
  );
}
